import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyForm = {
  name: '',
  contact_person: '',
  phone: '',
  email: '',
  fax: '',
  address: '',
  gst_no: '',
  ntn_no: '',
  cnic: '',
  account_id: '',
  is_active: true,
};

export default function PartyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    api.accounts.list({ per_page: 1000 }).then(({ data }) => {
      const list = Array.isArray(data.data) ? data.data : data.accounts || data;
      setAccounts(Array.isArray(list) ? list : list.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.parties.get(id).then(({ data }) => {
      const p = data.party || data;
      setForm({
        name: p.name || '',
        contact_person: p.contact_person || '',
        phone: p.phone || '',
        email: p.email || '',
        fax: p.fax || '',
        address: p.address || '',
        gst_no: p.gst_no || '',
        ntn_no: p.ntn_no || '',
        cnic: p.cnic || '',
        account_id: p.account_id || '',
        is_active: p.is_active !== false,
      });
    }).catch((err) => showError('Failed to load party', extractError(err))).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Party name is required';
    if (!form.phone) errs.phone = 'Phone is required';
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (!form.address) errs.address = 'Address is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const partyPayload = {
        type: 'customer',
        name: form.name,
        contact_person: form.contact_person,
        phone: form.phone,
        email: form.email,
        fax: form.fax,
        address: form.address,
        gst_no: form.gst_no,
        ntn_no: form.ntn_no,
        cnic: form.cnic,
        account_id: form.account_id ? Number(form.account_id) : undefined,
        is_active: form.is_active,
      };

      if (isEdit) {
        await api.parties.update(id, partyPayload);
        toast('success', '', 'Party updated successfully');
      } else {
        await api.parties.create(partyPayload);
        toast('success', '', 'Party created successfully');
      }
      navigate('/parties');
    } catch (err) {
      showError('Failed to save party', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></div>;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Party' : 'New Party'}
        subtitle={isEdit ? `Editing ${form.name}` : 'Create a new party. Link an existing account or one will be auto-created.'}
        crumbs={[{ label: 'Finance', to: '/parties' }, { label: 'Parties', to: '/parties' }, { label: isEdit ? 'Edit' : 'New Party' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/parties')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> {isEdit ? 'Update' : 'Create'} Party</>}
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-header"><div className="card-title">Basic Information</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Party Name <span className="req">*</span></label>
              <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { set('name', e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="Company or individual name" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Contact Person</label>
              <input className="form-control" value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} placeholder="Contact person name" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'end', paddingBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                Active party
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Link Existing Account <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <SearchableSelect
              options={[{ id: '', name: '-- Auto-create new account --' }, ...accounts]}
              value={form.account_id}
              onChange={(v) => set('account_id', v)}
              placeholder="Select existing account to link"
              getLabel={(a) => a.name}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>If left empty, a new account will be auto-created from Settings.</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Contact Details</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Phone <span className="req">*</span></label>
              <input className={`form-control ${errors.phone ? 'is-invalid' : ''}`} value={form.phone} onChange={(e) => { set('phone', e.target.value); if (errors.phone) setErrors((p) => ({ ...p, phone: '' })); }} placeholder="Phone number" />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Email <span className="req">*</span></label>
              <input className={`form-control ${errors.email ? 'is-invalid' : ''}`} type="email" value={form.email} onChange={(e) => { set('email', e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: '' })); }} placeholder="email@company.com" />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Fax</label>
              <input className="form-control" value={form.fax} onChange={(e) => set('fax', e.target.value)} placeholder="Fax number" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address <span className="req">*</span></label>
            <input className={`form-control ${errors.address ? 'is-invalid' : ''}`} value={form.address} onChange={(e) => { set('address', e.target.value); if (errors.address) setErrors((p) => ({ ...p, address: '' })); }} placeholder="Full address" />
            {errors.address && <div className="invalid-feedback">{errors.address}</div>}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Tax & Registration</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">GST No.</label>
              <input className="form-control" value={form.gst_no} onChange={(e) => set('gst_no', e.target.value)} placeholder="GST registration number" />
            </div>
            <div className="form-group">
              <label className="form-label">NTN No.</label>
              <input className="form-control" value={form.ntn_no} onChange={(e) => set('ntn_no', e.target.value)} placeholder="NTN registration number" />
            </div>
            <div className="form-group">
              <label className="form-label">CNIC</label>
              <input className="form-control" value={form.cnic} onChange={(e) => set('cnic', e.target.value)} placeholder="e.g. 42101-1234567-8" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
